import type { UiLang } from "@imagora/contracts";
import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { formatDate, formatDateTime, formatNumber, formatRelative } from "../lib/format";
import en from "./en.json";
import { formatMessage, humanizeCode, type Dictionary, type Params } from "./format";
import type { MessageKey, PluralKey, TextKey } from "./keys";

const LOADERS: Record<UiLang, () => Promise<{ default: Dictionary }>> = {
  ro: () => import("./ro.json"),
  bg: () => import("./bg.json"),
  hu: () => import("./hu.json"),
  en: async () => ({ default: en }),
};

const loaded = new Map<UiLang, Dictionary>();

/** Each language is its own chunk. English ships with the app, so a chunk that fails to load still leaves readable text. */
export async function loadLanguage(lang: UiLang): Promise<Dictionary> {
  const cached = loaded.get(lang);
  if (cached) return cached;
  try {
    const { default: dictionary } = await LOADERS[lang]();
    loaded.set(lang, dictionary);
    return dictionary;
  } catch {
    return en;
  }
}

interface I18nValue {
  lang: UiLang;
  /** Asks to change the language; the provider's owner decides how. */
  setLang: (lang: UiLang) => void;
  t: {
    (key: TextKey, params?: Params): string;
    (key: PluralKey, params: Params & { count: number }): string;
  };
  /** For keys built from a server code. Falls back to a readable form of the code. */
  tCode: (prefix: string, code: string, params?: Params) => string;
  /** The same, but null when the code has no text. */
  tCodeOrNull: (prefix: string, code: string, params?: Params) => string | null;
  /** Formats in the tenant's timezone. */
  timeZone: string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider(props: { lang: UiLang; dictionary: Dictionary; timeZone: string; setLang: (lang: UiLang) => void; children: ReactNode }) {
  const { lang, dictionary, timeZone, setLang } = props;
  const value = useMemo<I18nValue>(() => {
    const lookup = (prefix: string, code: string, params?: Params) => {
      const message = dictionary[`${prefix}.${code}`];
      return message === undefined ? null : formatMessage(message, lang, params);
    };
    return {
      lang,
      setLang,
      timeZone,
      t: ((key: MessageKey, params?: Params) => {
        const message = dictionary[key];
        return message === undefined ? String(key) : formatMessage(message, lang, params);
      }) as I18nValue["t"],
      tCode: (prefix, code, params) => lookup(prefix, code, params) ?? humanizeCode(code),
      tCodeOrNull: lookup,
    };
  }, [lang, dictionary, timeZone, setLang]);
  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n outside I18nProvider");
  return value;
}

export function useT() {
  return useI18n().t;
}

/** Dates and numbers in the current language and the tenant's timezone. */
export function useFormat() {
  const { lang, timeZone } = useI18n();
  const relative = useCallback((iso: string) => formatRelative(iso, lang, Date.now()), [lang]);
  return useMemo(
    () => ({
      date: (iso: string) => formatDate(iso, lang, timeZone),
      dateTime: (iso: string) => formatDateTime(iso, lang, timeZone),
      number: (value: number) => formatNumber(value, lang),
      relative,
    }),
    [lang, timeZone, relative],
  );
}
